import {AccessCodeAlphabet, DeviceType, NfcCard, Occupancy, Payment, Platform} from "@variocube/app-api";
import {
    classNames,
    Dialog,
    DialogAlignment,
    DialogTitle,
    layout,
    PushButton,
    typography,
    useAsyncEffect,
    useCountdown,
    useDeactivateListener,
    useDevices,
    useIdle,
    usePreventAutoDeactivate,
    View,
    WizardProgress,
    WizardStep
} from "@variocube/cube-ui";
import {DateTime} from "luxon";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {style, stylesheet} from "typestyle";
import {
    Accessibility,
    Availability,
    BookingOccupancyContent,
    BookingPlan,
    BookingUnit,
    Cart,
    Discount,
    PaymentOptions,
    PricingMode
} from "./booking";
import {BookingCodeStep, ContactData} from "./BookingCodeStep";
import {CartStep} from "./CartStep";
import {DeliveryMethod} from "./delivery-method";
import {PaymentStep} from "./PaymentStep";
import {formatCurrency} from "./utils";
import {useLocalization} from "../../i18n";
import {CartOnlineBooking} from "./CartOnlineBooking";

interface BookingFlowProps {
    platform: Platform;
    plans: BookingPlan[];
    onlineBookingUrl?: string;
    availability: Availability;
    paymentOptions: PaymentOptions;
    onDone: (occupancy?: Occupancy) => void;
    enablePhone?: boolean;
    enableEmail?: boolean;
    enableDisplay?: boolean;
    accessibleFrom: string;
    accessibleUntil: string;
    accessibility: Accessibility;
    termsUrl?: string;
    privacyPolicyUrl?: string;
    currency: string;
    bookingUnit: BookingUnit;
    pricingMode: PricingMode;
    discounts?: Record<string, Discount>;
    className?: string;
}

enum BookingStep {
    Cart,
    BookingCode,
    Payment,
    Done
}

export function BookingFlow(props: BookingFlowProps) {
    const {
        plans,
        onlineBookingUrl = "",
        accessibleFrom,
        accessibleUntil,
        accessibility,
        availability,
        enableDisplay,
        enableEmail,
        enablePhone,
        onDone,
        paymentOptions,
        platform,
        termsUrl,
        privacyPolicyUrl,
        currency,
        bookingUnit,
        pricingMode,
        discounts,
        className
    } = props;

    const [cart, setCart] = useState<Cart>();
    const [phone, setPhone] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [occupancy, setOccupancy] = useState<Occupancy>();
    const [verified, setVerified] = useState(false);

    const {t, language} = useLocalization();

    usePreventAutoDeactivate();

    const idle = useIdle(60);
    const left = useCountdown(30, idle);

    // compute current step
    const step = useMemo(() => {
        if (!cart || !occupancy) {
            return BookingStep.Cart;
        }
        if (!verified) {
            return BookingStep.BookingCode;
        }
        if (cart.amount > 0) {
            return BookingStep.Payment;
        }
        return BookingStep.Done;
    }, [cart, occupancy, verified]);


    const handleCart = useCallback(async (cart: Cart) => {
        try {
            // create occupancy
            const occupancy = await platform.occupy({
                type: cart.boxType,
                accessCodeShape: {
                    alphabet: AccessCodeAlphabet.OneToNine,
                    length: 6
                },
                content: {
                    booking: {
                        ...cart,
                        email,
                        phone,
                        locale: language,
                        from: cart.from.toISO(),
                        until: cart.until.toISO(),
                        created: DateTime.local().toISO(),
                        discount: cart.discount,
                    }
                } as BookingOccupancyContent,
            });

            console.log("Created occupancy", occupancy);

            // remember occupancy
            setCart(cart);
            setOccupancy(occupancy);
        }
        catch (error) {
            console.error("Unable to occupy box. Bailing out.", error);
            onDone();
        }
    }, [platform, accessibleUntil, onDone, email, phone]);

    const deliveryMethods = useMemo(() => ([
            enablePhone && DeliveryMethod.phone,
            enableEmail && DeliveryMethod.email,
            enableDisplay && DeliveryMethod.display
        ].filter(method => !!method) as DeliveryMethod[]
    ), [enablePhone, enableEmail, enableDisplay]);

    const cancelOccupancy = useCallback(async () => {
        if (occupancy) {
            try {
                await platform.cancelOccupancy(occupancy.uuid);
                console.log("Canceled occupancy", occupancy);
            }
            catch (error) {
                console.warn("Error cancelling occupancy. This is okay if occupancy timed out");
            }
            setOccupancy(undefined);
        }
    }, [platform, occupancy]);

    const handleCancel = useCallback(async () => {
        await cancelOccupancy();
        onDone();
    }, [cancelOccupancy, onDone]);

    const handleBackToCart = useCallback(async () => {
        await cancelOccupancy();
        setCart(undefined);
    }, [cancelOccupancy]);

    useAsyncEffect(async () => {
        if (left < 0) {
            await handleCancel();
        }
    }, [handleCancel, left]);

    const handleDeactivate = useCallback(async () => {
        if (step == BookingStep.Payment) {
            throw new Error("Cannot deactivate during payment");
        }
        await cancelOccupancy();
    }, [step, cancelOccupancy]);

    useDeactivateListener(handleDeactivate);

    const handleDone = useCallback((o: Occupancy) => onDone(o), [onDone]);

    const handlePaymentApproved = useCallback(async (payment?: Payment, card?: NfcCard) => {
        if (!occupancy) {
            throw new Error("No occupancy found.");
        }

        const booking = {
            ...occupancy.content.booking,
            cardUid: card?.uid,
            receiptText: payment?.receiptText,
            merchantReceiptText: payment?.merchantReceiptText,
            merchantTransactionUrl: payment?.merchantTransactionUrl,
            phone,
            email
        }
        await platform.confirmOccupancy(occupancy.uuid, {booking}, true);
    }, [occupancy, platform, phone, email]);

    const handlePaymentConfirmed = useCallback(() => {
        if (!occupancy) {
            throw new Error("No occupancy found.");
        }

        handleDone(occupancy);
    }, [handleDone, occupancy])

    const handleVerified = useCallback(async (contactData: ContactData, manualCode?: string) => {
        if (!occupancy) {
            throw new Error("No occupancy found.");
        }

        const flowOccupancy = occupancy;

        if (manualCode) {
            console.log('Manual code provided. Changing access code.');
            await platform.changeAccessCode(flowOccupancy.uuid, manualCode);
            flowOccupancy.accessCode = manualCode;
            setOccupancy(flowOccupancy);
        }

        if (cart!.amount == 0) {
            console.log("Amount is zero, skipping payment.");

            const booking = {
                ...flowOccupancy.content.booking,
                phone: contactData.phone,
                email: contactData.email
            }
            await platform.confirmOccupancy(flowOccupancy.uuid, {booking}, true);

            handleDone(flowOccupancy);
        }
        else {
            setEmail(contactData.email);
            setPhone(contactData.phone);
            setVerified(true);
        }
    }, [cart, handlePaymentApproved, handleDone, occupancy, platform]);

    const handlePaymentConfirmationFailed = useCallback(async () => {
        await platform.endOccupancy(occupancy!.uuid);
    }, [platform, occupancy]);

    const devices = useDevices();
    const hasPaymentTerminal = useMemo(() => Boolean(devices.find(device => device.types.indexOf(DeviceType.PaymentTerminal) >= 0)), [devices]);

    const displayOnlineBookingInfo = useMemo(() => {
        if (hasPaymentTerminal || !onlineBookingUrl) {
            return false;
        }
        switch (pricingMode) {
            case "Unit":
                return plans.every(p => p.units.every(u => u.price > 0));
            case "Package":
                return plans.every(p => p.packages.every(u => u.price > 0));
        }
    }, [plans, onlineBookingUrl, pricingMode, hasPaymentTerminal])

    if (displayOnlineBookingInfo) {
        return (
            <View className={className}>
                <WizardProgress>
                    <WizardStep title fill>
                        <div className={styles.stepName}>{t("BookLocker")}</div>
                    </WizardStep>
                </WizardProgress>

                <CartOnlineBooking onlineBookingUrl={onlineBookingUrl} />
            </View>
        );
    }

    return (
        <View className={className}>
            <WizardProgress>
                <WizardStep title>
                    <div className={styles.stepName}>{t("BookLocker")}</div>
                </WizardStep>
                <WizardStep active={step == BookingStep.Cart} done={step > BookingStep.Cart}>
                    <div className={styles.stepName}>{t("SizeAndDuration")}</div>
                    {cart && (
                        <div className={styles.stepSummary}>
                            {`${t("Size")} ${cart.boxType} | ${t(bookingUnit)} ${cart.units}`}
                        </div>
                    )}
                </WizardStep>
                <WizardStep active={step == BookingStep.BookingCode} done={step > BookingStep.BookingCode}>
                    <div className={styles.stepName}>{t("BookingCode")}</div>
                    <div
                        className={styles.stepSummary}>{verified && (phone || email || t("Displayed"))}</div>
                </WizardStep>
                {(!cart || cart.amount > 0) && (
                    <WizardStep active={step == BookingStep.Payment} done={step > BookingStep.Payment}>
                        <div className={styles.stepName}>{t("Payment")}</div>
                        {cart && (
                            <div className={styles.stepSummary}>
                                {t("Amount")} {formatCurrency(cart.amount, currency, language)}
                            </div>
                        )}
                    </WizardStep>
                )}
                <WizardStep fill>

                </WizardStep>
            </WizardProgress>

            {step == BookingStep.Cart && (
                <CartStep
                    platform={platform}
                    plans={plans}
                    availability={availability}
                    maxAmount={paymentOptions.maxAmount}
                    accessibleFrom={accessibleFrom}
                    accessibleUntil={accessibleUntil}
                    accessibility={accessibility}
                    onCart={handleCart}
                    onCancel={handleCancel}
                    termsUrl={termsUrl}
                    privacyPolicyUrl={privacyPolicyUrl}
                    currency={currency}
                    bookingUnit={bookingUnit}
                    pricingMode={pricingMode}
                    discounts={discounts ?? {}}
                />
            )}
            {step == BookingStep.BookingCode && (
                <BookingCodeStep
                    methods={deliveryMethods}
                    boxNumber={occupancy!.boxNumber}
                    code={occupancy!.accessCode}
                    onVerified={handleVerified}
                    onBack={handleBackToCart}
                />
            )}
            {step == BookingStep.Payment && (
                <PaymentStep
                    platform={platform}
                    onCancel={handleCancel}
                    amount={cart!.amount}
                    currency={currency}
                    onPaymentApproved={handlePaymentApproved}
                    onPaymentConfirmed={handlePaymentConfirmed}
                    onPaymentConfirmationFailed={handlePaymentConfirmationFailed}
                />
            )}
            <Dialog open={idle} align={DialogAlignment.Center}>
                <div className={classNames(layout.paddingLarge, typography.alignCenter, style({ width: "24em"}))}>
                    <DialogTitle>{t("BookingContinueTitle")}</DialogTitle>
                    <p>{t("BookingAutoCancelMessage", {left})}</p>
                    <PushButton primary>
                        {t("BookingContinue")}
                    </PushButton>
                </div>
            </Dialog>
        </View>
    );
}


const styles = stylesheet({
    stepSummary: {
        fontWeight: "bold",
        maxWidth: "10em",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    stepName: {
        textTransform: "uppercase"
    }
});